//
// ============================================================================
// TYPES
// ============================================================================
//

/**
 * All valid chaos scenario names.
 * Single source of truth — the ChaosScenario type is derived from this array.
 */
const VALID_SCENARIOS = [
  "off",
  "random",
  "force-status",
  "force-timeout",
  "force-network-error",
  "force-empty",
] as const;

/**
 * Chaos scenario determines how errors are injected.
 *
 * - `off`                 — No chaos effects applied (default)
 * - `random`              — Original random mode: failureRate chance of random error + configurable delay
 * - `force-status`        — Always throw with the status code from NUXT_PUBLIC_CHAOS_FORCE_STATUS
 * - `force-timeout`       — Always simulate a request timeout
 * - `force-network-error` — Always simulate a network/connectivity failure
 * - `force-empty`         — Every matching request returns a typed empty response (zero items)
 */
export type ChaosScenario = (typeof VALID_SCENARIOS)[number];

/**
 * Sentinel error thrown by `applyChaos` when the `force-empty` scenario is active.
 *
 * Unlike other chaos errors, this does NOT represent a failure — it signals that
 * the composable should return a typed empty response (0 items, empty arrays)
 * instead of making a real API call. Composables catch this via `isChaosEmptySignal()`
 * before the generic `classifyError()` path.
 */
export class ChaosEmptySignal extends Error {
  /** Discriminator for `isChaosEmptySignal` type guard */
  readonly __chaosEmpty = true as const;

  constructor() {
    super("Chaos force-empty — return typed empty response");
    this.name = "ChaosEmptySignal";
  }
}

/**
 * Type guard to identify `ChaosEmptySignal` in catch blocks.
 *
 * @param err - Unknown error from a catch block
 * @returns `true` if the error is a ChaosEmptySignal
 */
export function isChaosEmptySignal(err: unknown): err is ChaosEmptySignal {
  return err instanceof ChaosEmptySignal;
}

/**
 * HTTP status codes that can appear in real API responses.
 * Used by the `random` scenario to pick a realistic error.
 */
const RANDOM_ERROR_STATUS_CODES = [400, 401, 403, 404, 408, 429, 500, 502, 503, 504] as const;

//
// ============================================================================
// MODULE STATE
// ============================================================================
//

/** Ensures the chaos activation log fires exactly once per page load. */
let _chaosLogged = false;

/** Counts matched requests (endpoint passes the pattern filter) across the page load. */
let _matchedRequestCount = 0;

//
// ============================================================================
// PURE HELPERS (exported for unit testing)
// ============================================================================
//

/**
 * Parse and validate a scenario string from the environment.
 *
 * This is the only place runtime validation matters — the input is an
 * untyped env var string. Internal callers already have compile-time safety
 * via the ChaosScenario type.
 *
 * @param raw - Raw string value from the env var
 * @returns A valid ChaosScenario, defaulting to "off" for unknown values
 */
export function parseChaosScenario(raw: string | undefined): ChaosScenario {
  if (!raw) return "off";
  const normalised = raw.trim().toLowerCase();
  if ((VALID_SCENARIOS as readonly string[]).includes(normalised))
    return normalised as ChaosScenario;
  console.warn(`[useChaos] Unknown scenario "${raw}", falling back to "off".`);
  return "off";
}

/**
 * Determine whether chaos should fire for a given endpoint.
 *
 * @param endpoint - The API endpoint path being called
 * @param pattern  - Substring filter from the env var (empty = match all)
 * @returns `true` if the endpoint matches (or no pattern is set)
 */
export function endpointMatchesPattern(endpoint: string, pattern: string | undefined): boolean {
  if (!pattern || pattern.trim() === "") return true;
  return endpoint.toLowerCase().includes(pattern.trim().toLowerCase());
}

/**
 * Build a chaos error object that mirrors real $fetch errors.
 *
 * The error sets `statusCode` so that `useErrorBoundary.classifyError()` can
 * correctly classify it as auth / server / validation / etc.
 *
 * @param statusCode - HTTP status code to simulate
 * @returns Error with `statusCode` property
 */
export function buildChaosError(statusCode: number): Error & { statusCode: number } {
  const error = new Error(`HTTP ${statusCode} error — simulated by chaos mode`) as Error & {
    statusCode: number;
  };
  error.statusCode = statusCode;
  return error;
}

/**
 * Build a timeout error that mirrors real network timeouts.
 */
export function buildTimeoutError(): Error {
  return new Error("Request timeout — simulated by chaos mode");
}

/**
 * Build a network error that mirrors real connectivity failures.
 */
export function buildNetworkError(): Error {
  const error = new Error("Failed to fetch — simulated by chaos mode (network error)");
  error.name = "TypeError"; // Real fetch network errors are TypeErrors
  return error;
}

/**
 * Pick a random error status code from the realistic set.
 */
export function pickRandomStatusCode(): number {
  const index = Math.floor(Math.random() * RANDOM_ERROR_STATUS_CODES.length);
  return RANDOM_ERROR_STATUS_CODES[index] as number;
}

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Chaos Mode Testing Composable
 *
 * Simulates API failures, timeouts, and latency for robustness testing.
 * Supports both random chaos and deterministic scenario-based injection.
 *
 * Responsibilities:
 * - Scenario "random": probabilistic errors + configurable delay
 * - Scenario "force-status": always throw with a configured HTTP status code
 * - Scenario "force-timeout": always throw a timeout error
 * - Scenario "force-network-error": always throw a network connectivity error
 * - Scenario "force-empty": throw a ChaosEmptySignal so composables return typed empty data
 * - Endpoint pattern filtering: only inject on endpoints matching a substring
 * - Controlled via environment variables — zero code changes to reproduce any error
 * - Log all simulated effects to the console for debugging
 *
 * Environment variables (only relevant when NUXT_PUBLIC_CHAOS_ENABLED=true):
 *   NUXT_PUBLIC_CHAOS_SCENARIO          — one of: off, random, force-status, force-timeout, force-network-error, force-empty
 *   NUXT_PUBLIC_CHAOS_FORCE_STATUS      — HTTP status code for "force-status" scenario (e.g. 503)
 *   NUXT_PUBLIC_CHAOS_ENDPOINT_PATTERN  — substring filter; empty = all endpoints
 *   NUXT_PUBLIC_CHAOS_FAILURE_RATE      — failure probability for "random" scenario (0.0–1.0)
 *   NUXT_PUBLIC_CHAOS_DELAY_RATE        — probability of artificial delay for "random" scenario (0.0–1.0, default 0.3)
 *   NUXT_PUBLIC_CHAOS_DELAY_MIN / _MAX  — artificial delay range for "random" scenario
 *   NUXT_PUBLIC_CHAOS_SIMULATED_DELAY   — ms to wait before deterministic scenarios throw (default 600, 0 = instant)
 *   NUXT_PUBLIC_CHAOS_SKIP_COUNT        — matched requests to let through before injecting chaos (default 0, use 1+ to test snackbars)
 */
export const useChaos = () => {
  const config = useRuntimeConfig();
  const isChaosEnabled = config.public.features.chaos;

  // Resolve the active scenario
  const scenario: ChaosScenario = isChaosEnabled
    ? parseChaosScenario(config.public.chaos.scenario)
    : "off";

  // Log once per page load — not per composable instantiation
  if (import.meta.client && isChaosEnabled && scenario !== "off" && !_chaosLogged) {
    _chaosLogged = true;
    const patternInfo = config.public.chaos.endpointPattern
      ? ` | pattern: "${config.public.chaos.endpointPattern}"`
      : " | all endpoints";
    const extraInfo =
      scenario === "force-status" ? ` | status: ${config.public.chaos.forceStatus}` : "";
    console.warn(`[Chaos] Active — scenario: ${scenario}${extraInfo}${patternInfo}`);
  }

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Apply chaos effects to an API request.
   *
   * Called by `BaseClient` before every HTTP call. When chaos is active and the
   * endpoint matches the configured pattern, this method either throws a
   * simulated error or adds artificial latency, depending on the scenario.
   *
   * @param endpoint      - API endpoint being called (for pattern matching + logging)
   * @param componentName - Optional label for log messages
   * @throws Error — simulated HTTP, timeout, or network error
   */
  const applyChaos = async (endpoint: string, componentName?: string): Promise<void> => {
    // Gate: chaos disabled or SSR or scenario is off
    if (!isChaosEnabled || !import.meta.client || scenario === "off") {
      return;
    }

    // Gate: endpoint does not match the configured pattern
    if (!endpointMatchesPattern(endpoint, config.public.chaos.endpointPattern)) {
      return;
    }

    const label = componentName ? ` [${componentName}]` : "";

    // Gate: skip the first N matched requests so cached data can build up.
    // Use skipCount ≥ 1 to test the refresh-failure snackbar path:
    // first load succeeds (data cached), subsequent loads get chaos.
    const skipCount = config.public.chaos.skipCount;
    if (skipCount > 0) {
      _matchedRequestCount++;
      if (_matchedRequestCount <= skipCount) {
        console.warn(
          `[Chaos]${label} Skipping (${_matchedRequestCount}/${skipCount}) on ${endpoint}`,
        );
        return;
      }
    }

    // ── Deterministic scenarios ────────────────────────────────────────────

    // Deterministic scenarios use a configurable delay so Vue can paint
    // the loading skeleton before the error fires.
    const simulatedDelay = config.public.chaos.simulatedDelay;

    if (scenario === "force-status") {
      const status = config.public.chaos.forceStatus;
      if (simulatedDelay > 0) await new Promise((r) => setTimeout(r, simulatedDelay));
      console.warn(`[Chaos]${label} Force ${status} on ${endpoint}`);
      throw buildChaosError(status);
    }

    if (scenario === "force-timeout") {
      if (simulatedDelay > 0) await new Promise((r) => setTimeout(r, simulatedDelay));
      console.warn(`[Chaos]${label} Force timeout on ${endpoint}`);
      throw buildTimeoutError();
    }

    if (scenario === "force-network-error") {
      if (simulatedDelay > 0) await new Promise((r) => setTimeout(r, simulatedDelay));
      console.warn(`[Chaos]${label} Force network error on ${endpoint}`);
      throw buildNetworkError();
    }

    if (scenario === "force-empty") {
      if (simulatedDelay > 0) await new Promise((r) => setTimeout(r, simulatedDelay));
      console.warn(`[Chaos]${label} Force empty response on ${endpoint}`);
      throw new ChaosEmptySignal();
    }

    // ── Random scenario (original behaviour) ───────────────────────────────

    if (scenario === "random") {
      // Chance of error
      if (Math.random() < config.public.chaos.failureRate) {
        const roll = Math.random();

        if (roll < 0.1) {
          // 10% of failures → timeout
          console.warn(`[Chaos]${label} Random timeout on ${endpoint}`);
          throw buildTimeoutError();
        } else if (roll < 0.2) {
          // 10% of failures → network error
          console.warn(`[Chaos]${label} Random network error on ${endpoint}`);
          throw buildNetworkError();
        } else {
          // 80% of failures → random HTTP status
          const statusCode = pickRandomStatusCode();
          console.warn(`[Chaos]${label} Random ${statusCode} on ${endpoint}`);
          throw buildChaosError(statusCode);
        }
      }

      // Configurable chance of artificial delay (even when not failing)
      if (Math.random() < config.public.chaos.delayRate) {
        const delayRange = config.public.chaos.delayMax - config.public.chaos.delayMin;
        const delay = Math.floor(Math.random() * delayRange) + config.public.chaos.delayMin;
        console.warn(`[Chaos]${label} +${delay}ms delay on ${endpoint}`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  };

  return {
    /** Whether the chaos feature flag is turned on */
    isChaosEnabled,
    /** The resolved scenario (off if chaos is disabled) */
    activeScenario: scenario,
    /** Inject chaos effects — called by BaseClient before every request */
    applyChaos,
  };
};
